import Controller, { inject as controller } from "@ember/controller";
import EmberObject, { action, computed } from "@ember/object";
import { dependentKeyCompat } from "@ember/object/compat";
import { service } from "@ember/service";
import { trustHTML } from "@ember/template";
import { isEmpty } from "@ember/utils";
import { Promise } from "rsvp";
import { ajax } from "discourse/lib/ajax";
import BulkSelectHelper from "discourse/lib/bulk-select-helper";
import { iconHTML } from "discourse/lib/icon-library";
import Bookmark from "discourse/models/bookmark";
import { i18n } from "discourse-i18n";

export default class UserActivityBookmarksController extends Controller {
  @service router;
  @controller application;
  @controller user;

  queryParams = ["q"];

  /** @type {string | null | undefined} */
  q = null;
  loading = false;
  loadingMore = false;
  permissionDenied = false;

  bulkSelectHelper = new BulkSelectHelper(this);

  @computed("q")
  get searchTerm() {
    return this._searchTerm !== undefined ? this._searchTerm : this.q;
  }

  set searchTerm(value) {
    this._searchTerm = value;
  }

  @computed("q")
  get inSearchMode() {
    return !isEmpty(this.q);
  }

  @dependentKeyCompat
  get noContent() {
    return this.model?.bookmarks?.length === 0;
  }

  @computed()
  get emptyStateBody() {
    return trustHTML(
      i18n("user.no_bookmarks_body", {
        icon: iconHTML("bookmark"),
      })
    );
  }

  @computed("inSearchMode", "noContent")
  get userDoesNotHaveBookmarks() {
    return !this.inSearchMode && this.noContent;
  }

  @computed("inSearchMode", "noContent")
  get nothingFound() {
    return this.inSearchMode && this.noContent;
  }

  @action
  search() {
    this.router.transitionTo({
      queryParams: { q: this._searchTerm },
    });
  }

  @action
  reload() {
    this.send("triggerRefresh");
  }

  @action
  loadMore() {
    if (this.loadingMore) {
      return Promise.resolve();
    }

    this.set("loadingMore", true);

    return this._loadMoreBookmarks(this.q)
      .then((response) => this._processLoadResponse(this.q, response))
      .catch(() => this._bookmarksListDenied())
      .finally(() => this.set("loadingMore", false));
  }

  @action
  updateAutoAddBookmarksToBulkSelect(value) {
    this.bulkSelectHelper.autoAddBookmarksToBulkSelect = value;
  }

  transform(bookmark) {
    const bookmarkModel = Bookmark.create(bookmark);
    bookmarkModel.topicStatus = EmberObject.create({
      closed: bookmark.closed,
      archived: bookmark.archived,
      is_warning: bookmark.is_warning,
      pinned: false,
      unpinned: false,
      invisible: bookmark.invisible,
    });
    return bookmarkModel;
  }

  _loadMoreBookmarks(searchQuery) {
    if (!this.model.loadMoreUrl) {
      return Promise.resolve();
    }

    let moreUrl = this.model.loadMoreUrl;
    if (searchQuery) {
      const delimiter = moreUrl.includes("?") ? "&" : "?";
      const q = encodeURIComponent(searchQuery);
      moreUrl += `${delimiter}q=${q}`;
    }

    return ajax({ url: moreUrl });
  }

  _bookmarksListDenied() {
    this.set("permissionDenied", true);
  }

  async _processLoadResponse(searchTerm, response) {
    if (!response || !response.user_bookmark_list) {
      this.model.loadMoreUrl = null;
      return;
    }

    response = response.user_bookmark_list;
    this.model.searchTerm = searchTerm;
    this.model.loadMoreUrl = response.more_bookmarks_url;

    if (response.bookmarks) {
      const bookmarkModels = response.bookmarks.map(this.transform);
      await Bookmark.applyTransformations(bookmarkModels);
      this.model.bookmarks.push(...bookmarkModels);
      this.session.set("bookmarksModel", this.model);
    }
  }
}
